// src/pages/orderAssignment/MyWorkQueuePage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  Eye,
  AlertCircle,
  RefreshCw,
  User,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Store,
  Package,
  ChevronDown,
  Phone as PhoneIcon,
  Layers,
  Copy,
  Check,
  ExternalLink,
  Users as UsersIcon,
  ShieldAlert,
  StickyNote,
} from "lucide-react";
import {
  PageHeader,
  StatsCards,
  DataTable,
  FilterPanel,
  MultiSelectFilter,
} from "../../components/common";
import {
  getMyWorkQueue,
  getAssignmentStats,
} from "../../services/api/orderAssignments";
import { getAllOrderReferenceData } from "../../services/api/orderReference";
import {
  updateEcomOrder,
  bulkUpdateOrderStatuses,
} from "../../services/api/orders";
import { bulkAssignCreatedOrders } from "../../services/api/orderAssignments";
import { getShops } from "../../services/api/shops";
import { getUsers } from "../../services/api/users";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuth } from "../../hooks/useAuth";
import { useBusinessSettings } from "../../hooks/useBusinessSettings";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useAlert } from "../../contexts/AlertContext";
import { useExport } from "../../contexts/ExportContext";
import { formatCurrency } from "../../utils/orderCalculations";
import { formatDate, formatTime, formatDateTime } from "../../utils/dateUtils";
import { PaymentMethodIcon } from "../../utils/paymentMethodIcons";
import OrderDetailsModal from "./components/OrderDetailsModal";
import BulkStatusUpdateModal from "./components/BulkStatusUpdateModal";
import BulkAssignModal from "./components/BulkAssignModal";
import ProductSearchFilter from "./components/ProductSearchFilter";
import DateRangePicker from "../../components/ui/DateRangePicker/DateRangePicker";
import styles from "./MyWorkQueuePage.module.css";

const STORAGE_KEY = "myWorkQueue_selectedShopId";
const STORAGE_VIEW_KEY = "myWorkQueue_selectedView";
const CITY_UNKNOWN_ID = 9999;
const SEARCH_DEBOUNCE_MS = 800;

const CALL_ATTEMPTS_OPTIONS = [
  { key: "0",  label: "0 Attempts"   },
  { key: "1",  label: "1 Attempt"    },
  { key: "2",  label: "2 Attempts"   },
  { key: "3",  label: "3 Attempts"   },
  { key: "4",  label: "4 Attempts"   },
  { key: "5",  label: "5 Attempts"   },
  { key: "6",  label: "6 Attempts"   },
  { key: "7",  label: "7 Attempts"   },
  { key: "7+", label: "More than 7" },
];

const normalizeSearchQuery = (query) => {
  if (!query) return query;
  const trimmed = query.trim();
  if (/^0\d{3,9}$/.test(trimmed)) return trimmed.slice(1);
  return trimmed;
};

const MyWorkQueuePage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { startExport, isExporting } = useExport();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { settings: orderDispatchSettings } = useBusinessSettings("orderDispatch");

  const canViewAll    = hasPermission("view_full_work_queue");
  const canAssign     = hasPermission("assign_orders");

  // ── Data ────────────────────────────────────────────────────────────
  const [orders, setOrders]                       = useState([]);
  const [stats, setStats]                         = useState(null);
  const [isLoading, setIsLoading]                 = useState(true);
  const [loadingError, setLoadingError]           = useState(null);
  const [selectedOrders, setSelectedOrders]       = useState([]);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState([]);
  const [showDetailsModal, setShowDetailsModal]   = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [isBulkAssigning, setIsBulkAssigning]     = useState(false);

  // Copy state
  const [copiedOrderId, setCopiedOrderId]   = useState(null);
  const [copiedPhoneId, setCopiedPhoneId]   = useState(null);
  const [noteTooltip, setNoteTooltip]       = useState(null);

  // Shop / view
  const [userShops, setUserShops]         = useState([]);
  const [selectedShopId, setSelectedShopId] = useState("");
  const [selectedView, setSelectedView]   = useState("assigned");

  // All users (for assigned-user filter & bulk assign)
  const [allUsers, setAllUsers] = useState([]);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  // Sorting — default: callAttempts ASC
  const [sorting, setSorting] = useState({
    orderBy: "callAttempts",
    orderDirection: "ASC",
  });

  // Filters
  const [showFilters, setShowFilters]   = useState(false);
  const [searchInput, setSearchInput]   = useState("");
  const [filters, setFilters] = useState({
    search: "",
    statusKey: "",
    statusReasonKey: "",
    paymentVerified: "",   // "" | "true" | "false"
    productIds: [],
    grandTotalMin: "",
    grandTotalMax: "",
    callAttempts: [],
    assignedUserId: "",   // "" | "unassigned" | "<userId>"
    confirmedByUserIds: [],
    confirmedAtRange: { from: "", to: "" },
  });

  // Reference data
  const [referenceData, setReferenceData] = useState({
    shops: [],
    orderStatuses: [],
    orderStatusReasons: {},
    products: [],
    cities: [],
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev;
        return { ...prev, search: searchInput };
      });
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Initial load
  useEffect(() => {
    loadUserShops();
    loadReferenceData();
  }, []);

  // Load users when view changes to "all" and user has assign_orders
  useEffect(() => {
    if (selectedView === "all" && canAssign && allUsers.length === 0) {
      loadAllUsers();
    }
  }, [selectedView, canAssign]);

  // Fetch orders + stats when deps change
  useEffect(() => {
    if (selectedShopId) {
      fetchOrders();
      fetchStats();
    }
  }, [
    selectedShopId,
    selectedView,
    pagination.currentPage,
    pagination.pageSize,
    sorting.orderBy,
    sorting.orderDirection,
    filters.search,
    filters.statusKey,
    filters.statusReasonKey,
    filters.paymentVerified,
    filters.productIds,
    filters.grandTotalMin,
    filters.grandTotalMax,
    filters.callAttempts,
    filters.assignedUserId,
    filters.confirmedByUserIds,
    filters.confirmedAtRange,
  ]);

  // Users for the Confirmed By filter (both views)
  useEffect(() => {
    if (allUsers.length === 0) loadAllUsers();
  }, []);

  const loadAllUsers = async () => {
    try {
      const res = await getUsers({ limit: 1000 });
      setAllUsers(res?.users || []);
    } catch {
      // non-critical
    }
  };

  const loadUserShops = async () => {
    try {
      const response = await getShops({ assigned: true, type: "online,dropshipper" });
      const shops = Array.isArray(response) ? response : response.shops || [];
      setUserShops(shops);

      const savedShopId = localStorage.getItem(STORAGE_KEY);
      if (savedShopId && shops.find((s) => s.id === parseInt(savedShopId))) {
        setSelectedShopId(savedShopId);
      } else if (shops.length > 0) {
        const defaultShop = shops.find((s) => s.isDefault) || shops[0];
        setSelectedShopId(defaultShop.id.toString());
      }

      const savedView = localStorage.getItem(STORAGE_VIEW_KEY);
      if (savedView && (savedView === "all" || savedView === "assigned")) {
        if (savedView === "all" && canViewAll) {
          setSelectedView("all");
        } else {
          setSelectedView("assigned");
        }
      }
    } catch (err) {
      console.error("Failed to load shops:", err);
      showError(t("errors.failedToLoadShops"));
    }
  };

  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
    localStorage.setItem(STORAGE_KEY, shopId);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setFilters((prev) => ({
      ...prev,
      statusKey: "",
      statusReasonKey: "",
      productIds: [],
      assignedUserId: "",
    }));
  };

  const handleViewChange = (view) => {
    setSelectedView(view);
    localStorage.setItem(STORAGE_VIEW_KEY, view);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Reset assignedUserId filter when switching away from "all"
    if (view !== "all") {
      setFilters((prev) => ({ ...prev, assignedUserId: "" }));
    }
  };

  const loadReferenceData = async () => {
    try {
      const data = await getAllOrderReferenceData();
      setReferenceData({
        shops: data.shops || [],
        orderStatuses: data.orderStatuses || [],
        orderStatusReasons: data.orderStatusReasons || {},
        products: [],
        cities: data.cities || [],
      });
    } catch (err) {
      console.error("Failed to load reference data:", err);
    }
  };

  const fetchStats = async () => {
    if (!selectedShopId) return;
    try {
      const params = { shopId: selectedShopId, view: selectedView };
      const response = await getAssignmentStats(params);
      setStats(response.stats);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      setStats(null);
    }
  };

  const buildQueueParams = () => {
    const params = {
      shopId: selectedShopId,
      view: selectedView,
      orderBy: sorting.orderBy,
      orderDirection: sorting.orderDirection,
    };

    if (filters.search)                   params.search            = normalizeSearchQuery(filters.search);
    if (filters.statusKey)                params.statusKey         = filters.statusKey;
    if (filters.statusReasonKey)          params.statusReasonKey   = filters.statusReasonKey;
    if (filters.paymentVerified)          params.paymentVerified   = filters.paymentVerified;
    if (filters.productIds.length > 0)    params.productIds        = filters.productIds.join(",");
    if (filters.grandTotalMin)            params.grandTotalMin     = filters.grandTotalMin;
    if (filters.grandTotalMax)            params.grandTotalMax     = filters.grandTotalMax;
    if (filters.callAttempts.length > 0)  params.callAttempts      = filters.callAttempts.join(",");
    if (filters.assignedUserId)           params.assignedUserId    = filters.assignedUserId;
    if (filters.confirmedByUserIds.length > 0) params.confirmedByUserIds = filters.confirmedByUserIds.join(",");
    if (filters.confirmedAtRange.from)    params.confirmedAtFrom   = filters.confirmedAtRange.from;
    if (filters.confirmedAtRange.to)      params.confirmedAtTo     = filters.confirmedAtRange.to;
    return params;
  };

  const fetchOrders = async () => {
    if (!selectedShopId) return;
    try {
      setIsLoading(true);
      setLoadingError(null);

      const response = await getMyWorkQueue({
        ...buildQueueParams(),
        page: pagination.currentPage,
        limit: pagination.pageSize,
      });
      setOrders(response.orders);
      setPagination((prev) => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setLoadingError(t("ecomOrders.failedToLoadOrder"));
      if (hasLoadedOnce) showError(t("ecomOrders.failedToLoadOrder"));
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const handleStatCardClick = (statusKey) => {
    setFilters((prev) => ({ ...prev, statusKey, statusReasonKey: "", paymentVerified: statusKey === "confirmed" && orderDispatchSettings?.stopUnverifiedDispatch ? "true" : "" }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleConfirmedPaymentCardClick = (paymentVerified) => {
    setFilters((prev) => ({ ...prev, statusKey: "confirmed", statusReasonKey: "", paymentVerified }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...newFilters, search: prev.search }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setSearchInput(searchValue);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setFilters({
      search: "",
      statusKey: "",
      statusReasonKey: "",
      paymentVerified: "",
      productIds: [],
      grandTotalMin: "",
      grandTotalMax: "",
      callAttempts: [],
      assignedUserId: "",
      confirmedByUserIds: [],
      confirmedAtRange: { from: "", to: "" },
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return (
      searchInput !== "" ||
      filters.search !== "" ||
      filters.statusKey !== "" ||
      filters.statusReasonKey !== "" ||
      filters.paymentVerified !== "" ||
      filters.productIds.length > 0 ||
      filters.grandTotalMin !== "" ||
      filters.grandTotalMax !== "" ||
      filters.callAttempts.length > 0 ||
      filters.assignedUserId !== "" ||
      filters.confirmedByUserIds.length > 0 ||
      filters.confirmedAtRange.from !== "" ||
      filters.confirmedAtRange.to !== ""
    );
  };

  const handleViewOrder = (order, assignedUsers = []) => {
    setSelectedAssignedUsers(assignedUsers || []);
    setSelectedOrderDetails(order);
    setShowDetailsModal(true);
  };

  const handleUpdateOrder = (updatedOrder) => {
    setOrders((prevOrders) =>
      prevOrders.map((assignment) =>
        assignment.order.id === updatedOrder.id
          ? { ...assignment, order: updatedOrder }
          : assignment
      )
    );
    if (selectedOrderDetails && selectedOrderDetails.id === updatedOrder.id) {
      setSelectedOrderDetails(updatedOrder);
    }
  };

  const handleBulkStatusUpdate = () => {
    if (selectedOrders.length === 0) {
      showError(t("orderAssignment.selectAtLeastOneOrder"));
      return;
    }
    setShowBulkUpdateModal(true);
  };

  const handleBulkAssign = () => {
    if (selectedOrders.length === 0) {
      showError(t("orderAssignment.selectAtLeastOneOrder"));
      return;
    }
    // Ensure users are loaded
    if (allUsers.length === 0) {
      loadAllUsers();
    }
    setShowBulkAssignModal(true);
  };

  const handleBulkAssignConfirm = async (userIds) => {
    setIsBulkAssigning(true);
    try {
      const response = await bulkAssignCreatedOrders({
        orderIds: selectedOrders,
        userIds,
        shopId: parseInt(selectedShopId),
      });
      success(
        t("orderAssignment.bulkAssignSuccess", { count: response.assignedOrders }) ||
          `${response.assignedOrders} order${response.assignedOrders !== 1 ? "s" : ""} assigned successfully`,
        { title: t("orderAssignment.assignmentComplete") || "Assignment Complete", duration: 4000 }
      );
      setShowBulkAssignModal(false);
      setSelectedOrders([]);
      fetchOrders();
      fetchStats();
    } catch (err) {
      showError(err.response?.data?.message || t("orderAssignment.assignmentFailed") || "Failed to assign orders");
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleStatusUpdate = async (orderId, statusData) => {
    try {
      await updateEcomOrder(orderId, { ...statusData, incrementCallAttempts: true });
      success(t("orderAssignment.orderStatusUpdated"));
      fetchOrders();
      fetchStats();
      setSelectedOrders([]);
      setShowDetailsModal(false);
    } catch (err) {
      console.error("Failed to update order status:", err);
      showError(err.response?.data?.message || t("orderAssignment.failedToUpdateStatus"));
    }
  };

  const handleBulkStatusUpdateComplete = async (statusData) => {
    try {
      const response = await bulkUpdateOrderStatuses({
        orderIds: selectedOrders,
        updates: { ...statusData, incrementCallAttempts: true },
      });
      if (response.success) {
        success(t("orderAssignment.bulkUpdateSuccess", { count: response.summary.successCount }));
      } else {
        showError(t("orderAssignment.failedToUpdateSomeOrders"));
      }
      fetchOrders();
      fetchStats();
      setSelectedOrders([]);
      setShowBulkUpdateModal(false);
    } catch (err) {
      console.error("Failed to bulk update orders:", err);
      showError(t("orderAssignment.failedToUpdateOrders"));
    }
  };

  const MAX_EXPORT_RECORDS = 150000;
  const EXPORT_BATCH_SIZE  = 500;

  const handleExport = async () => {
    if (isExporting) {
      showError(t("ecomOrders.exportAlreadyRunning"));
      return;
    }

    const exportCount = Math.min(pagination.total || 0, MAX_EXPORT_RECORDS);
    const confirmed = await confirm(
      `${t("ecomOrders.exportConfirmMessage", { count: exportCount.toLocaleString() })}${
        (pagination.total || 0) > MAX_EXPORT_RECORDS
          ? ` ${t("ecomOrders.exportCapNote", { max: MAX_EXPORT_RECORDS.toLocaleString() })}`
          : ""
      }`,
      { title: t("ecomOrders.exportConfirmTitle"), confirmText: t("common.export"), cancelText: t("common.cancel") }
    );
    if (!confirmed) return;

    const params = buildQueueParams();
    startExport((job) => runWorkQueueExport(params, job), { title: t("ecomOrders.exportingOrders") });
  };

  const runWorkQueueExport = async (params, { signal, onTotal, onProgress }) => {
    const firstPage = await getMyWorkQueue({ ...params, page: 1, limit: 1 }, { signal });
    const total     = Math.min(firstPage.total || 0, MAX_EXPORT_RECORDS);

    if (total === 0) {
      throw new Error(t("ecomOrders.noOrdersToExport"));
    }

    onTotal(total);
    const allAssignments = [];
    const totalPages     = Math.ceil(total / EXPORT_BATCH_SIZE);

    for (let page = 1; page <= totalPages; page++) {
      if (signal.aborted) return;
      const response = await getMyWorkQueue({ ...params, page, limit: EXPORT_BATCH_SIZE }, { signal });
      allAssignments.push(...(response.orders || []));
      onProgress(Math.min(allAssignments.length, total));
    }
    if (signal.aborted) return;

    const headers = [
      t("orderAssignment.orderNumber"),
      t("orderAssignment.customerName"),
      t("orderAssignment.status"),
      t("orderAssignment.grandTotal"),
      t("orderAssignment.updatedAt"),
    ];
    const rows = allAssignments.slice(0, total).map((assignment) => {
      const order = assignment.order;
      return [
        order.orderNumber,
        order.customerName,
        order.OrderStatus?.label || order.orderStatusKey,
        order.grandTotal,
        formatDateTime(order.updatedAt),
      ];
    });
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `work-queue-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusIcon = (statusKey) => {
    const icons = {
      pending:       <Clock size={14} />,
      confirmed:     <CheckCircle size={14} />,
      not_confirmed: <XCircle size={14} />,
      cancelled:     <XCircle size={14} />,
      in_courier:    <Truck size={14} />,
    };
    return icons[statusKey] || <Clock size={14} />;
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return t("common.yearsAgo",   { count: Math.floor(interval) });
    interval = seconds / 2592000;
    if (interval > 1) return t("common.monthsAgo",  { count: Math.floor(interval) });
    interval = seconds / 86400;
    if (interval > 1) return t("common.daysAgo",    { count: Math.floor(interval) });
    interval = seconds / 3600;
    if (interval > 1) return t("common.hoursAgo",   { count: Math.floor(interval) });
    interval = seconds / 60;
    if (interval > 1) return t("common.minutesAgo", { count: Math.floor(interval) });
    return t("common.secondsAgo", { count: Math.floor(seconds) });
  };

  const isCityUnknown = (order) =>
    order.cityId === CITY_UNKNOWN_ID || order.City?.id === CITY_UNKNOWN_ID;

  const isHoldDateOverdue = (holdUntilDate) => {
    if (!holdUntilDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const holdDate = new Date(holdUntilDate);
    holdDate.setHours(0, 0, 0, 0);
    return holdDate <= today;
  };

  const getAvailableStatuses = () =>
    referenceData.orderStatuses.filter((s) => !s.isInPacking && !s.isDispatched);

  const selectedShop = userShops.find((s) => s.id === parseInt(selectedShopId));
  const isResellerDropshipper =
    selectedShop?.type === 'dropshipper' &&
    selectedShop?.resellerBusinessId != null &&
    parseInt(selectedShop.resellerBusinessId) === parseInt(user?.businessId);

  const handleCopyToClipboard = async (text, type, id) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "order") {
        setCopiedOrderId(id);
        setTimeout(() => setCopiedOrderId(null), 2000);
      } else if (type === "phone") {
        setCopiedPhoneId(id);
        setTimeout(() => setCopiedPhoneId(null), 2000);
      }
    } catch (err) {
      showError("Failed to copy to clipboard");
    }
  };

  const handleViewOrdersByPhone = (phoneNumber) => {
    window.open(`/orders?search=${encodeURIComponent(phoneNumber)}`, "_blank");
  };

  const showNoteTooltip = (e, text) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setNoteTooltip({ text, top: rect.bottom + 6, left: rect.left });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // ── Assigned-user filter options (only for "all" view + assign_orders) ──
  const assignedUserOptions = [
    { value: "unassigned", label: "Not Assigned" },
    ...allUsers.map((u) => ({
      value: String(u.id),
      label: `${u.name}${u.role?.name ? ` (${u.role.name})` : ""}`,
    })),
  ];

  // ── Stats cards ──────────────────────────────────────────────────────
  const statsCardsData = stats
    ? [
        {
          key: "total",
          label: t("orderAssignment.totalAssigned"),
          value: stats.totalAssigned,
          icon: Package,
          color: "#7F5CFF",
          onClick: () => handleStatCardClick(""),
        },
        ...stats.statusBreakdown.map((status) => ({
          key: status.statusKey,
          label: status.statusLabel,
          value: status.count,
          icon: () => getStatusIcon(status.statusKey),
          color: status.colorCode,
          bgColor: `${status.colorCode}15`,
          onClick: () => handleStatCardClick(status.statusKey),
        })),
        ...(orderDispatchSettings?.stopUnverifiedDispatch
          ? [
              {
                key: "confirmedNotVerified",
                label: t("orderAssignment.confirmedNotVerified"),
                value: stats.confirmedNotVerifiedCount || 0,
                icon: ShieldAlert,
                color: "#ef4444",
                onClick: () => handleConfirmedPaymentCardClick("false"),
                hidden: (stats.confirmedNotVerifiedCount || 0) === 0,
              },
            ]
          : []),
      ]
    : [];

  // ── Filter definitions ───────────────────────────────────────────────
  const filterDefinitions = [
    {
      key: "statusKey",
      label: t("orderAssignment.orderStatus"),
      type: "select",
      placeholder: t("orderAssignment.allStatuses"),
      options: referenceData.orderStatuses.map((status) => ({
        value: status.key,
        label: status.label,
      })),
    },
    {
      key: "statusReasonKey",
      label: t("orderAssignment.statusReason"),
      type: "select",
      placeholder: t("orderAssignment.allReasons"),
      options:
        filters.statusKey && referenceData.orderStatusReasons[filters.statusKey]
          ? referenceData.orderStatusReasons[filters.statusKey].map((reason) => ({
              value: reason.key,
              label: reason.label,
            }))
          : [],
      disabled:
        !filters.statusKey || !referenceData.orderStatusReasons[filters.statusKey],
    },
    // Assigned User filter — only shown in "all" view for users with assign_orders
    ...(selectedView === "all" && canAssign
      ? [
          {
            key: "assignedUserId",
            label: t("orderAssignment.assignedUser") || "Assigned User",
            type: "select",
            searchable: true,
            placeholder: t("orderAssignment.allUsers") || "All users",
            options: assignedUserOptions,
          },
        ]
      : []),
    {
      key: "confirmedByUserIds",
      label: t("ecomOrders.confirmedBy") || "Confirmed By",
      type: "custom",
      render: (value, onChange) => (
        <MultiSelectFilter
          options={allUsers.map((u) => ({ key: u.id, label: u.name }))}
          selectedValues={value || []}
          onChange={onChange}
          placeholder="All confirmers"
          searchPlaceholder="Search users..."
          valueKey="key"
          displayKey="label"
          searchKey="label"
        />
      ),
    },
    {
      key: "confirmedAtRange",
      label: t("ecomOrders.confirmedAt") || "Confirmed At",
      type: "custom",
      render: (value, onChange) => (
        <DateRangePicker
          value={value || { from: "", to: "" }}
          onChange={onChange}
          placeholder="All confirmation dates"
        />
      ),
    },
    {
      key: "callAttempts",
      label: t("orderAssignment.callAttempts"),
      type: "custom",
      render: (value, onChange) => (
        <MultiSelectFilter
          options={CALL_ATTEMPTS_OPTIONS}
          selectedValues={value || []}
          onChange={onChange}
          placeholder={t("orderAssignment.allAttempts") || "All attempts"}
          searchPlaceholder="Search attempts..."
          valueKey="key"
          displayKey="label"
          searchKey="label"
        />
      ),
    },
    {
      key: "productIds",
      label: t("orderAssignment.products"),
      type: "custom",
      render: (value, onChange) => (
        <ProductSearchFilter
          shopId={selectedShopId}
          selectedProductIds={value || []}
          onChange={onChange}
        />
      ),
    },
    {
      key: "grandTotalMin",
      label: t("orderAssignment.minGrandTotal"),
      type: "number",
      placeholder: "0.00",
      min: 0,
      step: 0.01,
    },
    {
      key: "grandTotalMax",
      label: t("orderAssignment.maxGrandTotal"),
      type: "number",
      placeholder: "0.00",
      min: 0,
      step: 0.01,
    },
  ];

  // ── Columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      key: "customerName",
      header: t("orderAssignment.customer"),
      sortable: true,
      width: "240px",
      render: (assignment) => {
        const order = assignment.order;
        const primaryPhone =
          order.phoneNumbers?.find((p) => p.isPrimary) || order.phoneNumbers?.[0];
        const hasPreviousOrders = primaryPhone && primaryPhone.previousOrders > 0;
        const isBlacklisted = primaryPhone?.isBlacklisted;
        const cityUnknown = isCityUnknown(order);
        const phoneHighlightClass = isBlacklisted
          ? styles.phoneBlacklisted
          : hasPreviousOrders
          ? styles.phoneWithHistory
          : "";
        return (
          <div className={styles.customerInfo}>
            <div className={styles.customerName}>
              <User size={14} />
              {truncateText(order.customerName, 20)}
              {order.specialNotes?.trim() && (
                <span
                  className={styles.specialNoteIcon}
                  aria-label={t("orderAssignment.specialNote")}
                  tabIndex={0}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={(e) => showNoteTooltip(e, order.specialNotes)}
                  onFocus={(e) => showNoteTooltip(e, order.specialNotes)}
                  onMouseLeave={() => setNoteTooltip(null)}
                  onBlur={() => setNoteTooltip(null)}
                >
                  <StickyNote size={13} />
                </span>
              )}
            </div>
            <div className={styles.customerAddress}>
              <MapPin size={12} />
              {order.City?.name || t("common.na")}
              {cityUnknown && (
                <span className={styles.cityUnknownBadge}>Incomplete</span>
              )}
            </div>
            {primaryPhone && (
              <div className={styles.phoneNumberRow}>
                <a
                  href={`tel:${primaryPhone.phoneNumber}`}
                  className={`${styles.phoneNumber} ${phoneHighlightClass}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <PhoneIcon size={12} />
                  {primaryPhone.phoneNumber}
                  {hasPreviousOrders && (
                    <span className={styles.previousOrdersBadge}>
                      {primaryPhone.previousOrders}
                    </span>
                  )}
                </a>
                <button
                  className={styles.copyButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyToClipboard(primaryPhone.phoneNumber, "phone", `${order.id}-phone`);
                  }}
                  title="Copy phone number"
                >
                  {copiedPhoneId === `${order.id}-phone` ? (
                    <Check size={12} className={styles.copiedIcon} />
                  ) : (
                    <Copy size={12} />
                  )}
                </button>
                {hasPreviousOrders && (
                  <button
                    className={styles.viewOrdersButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOrdersByPhone(primaryPhone.phoneNumber);
                    }}
                    title="View orders by this phone number"
                  >
                    <ExternalLink size={12} />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "products",
      header: t("orderAssignment.products"),
      width: "220px",
      render: (assignment) => (
        <div className={styles.productsInfo}>
          {assignment.order.orderItems?.map((item, idx) => (
            <div key={idx} className={styles.productItem}>
              {item.Product?.name} × {item.quantity}
            </div>
          ))}
        </div>
      ),
    },
    {
      key: "paymentMethods",
      header: t("orderAssignment.payments"),
      width: "70px",
      render: (assignment) => {
        const uniqueMethods = Array.from(
          (assignment.order.paymentRecords || [])
            .filter((pr) => pr.PaymentMethod?.key)
            .reduce((map, pr) => {
              const key = pr.PaymentMethod.key;
              const existing = map.get(key);
              // A method only counts as verified for the badge if every record using it is verified.
              const verified = existing ? existing.verified && !!pr.isVerified : !!pr.isVerified;
              map.set(key, { method: pr.PaymentMethod, verified });
              return map;
            }, new Map())
            .values()
        );
        if (uniqueMethods.length === 0) return <span className={styles.naText}>—</span>;
        return (
          <div className={styles.paymentMethodsCell}>
            {uniqueMethods.map(({ method, verified }) => (
              <PaymentMethodIcon
                key={method.key}
                iconFileName={method.iconFileName}
                label={method.label}
                width={45}
                verified={verified}
              />
            ))}
          </div>
        );
      },
    },
    {
      key: "grandTotal",
      header: t("orderAssignment.total"),
      sortable: true,
      width: "120px",
      align: "right",
      render: (assignment) => (
        <div className={styles.totalAmount}>
          {formatCurrency(assignment.order.grandTotal)}
        </div>
      ),
    },
    {
      key: "callAttempts",
      header: t("orderAssignment.callAttempts"),
      sortable: true,
      width: "110px",
      align: "center",
      render: (assignment) => (
        <div className={styles.callAttempts}>
          <PhoneIcon size={12} />
          <span>{assignment.order.callAttempts || 0}</span>
        </div>
      ),
    },
    {
      key: "updatedAt",
      header: t("orderAssignment.updatedAt"),
      sortable: true,
      width: "150px",
      render: (assignment) => (
        <div className={styles.assignedInfo}>
          <Calendar size={12} />
          {formatTimeAgo(assignment.order.updatedAt)}
        </div>
      ),
    },
    {
      key: "status",
      header: t("orderAssignment.status"),
      width: "180px",
      render: (assignment) => {
        const order = assignment.order;
        const requiresHoldDate = order.OrderStatus?.requiresHoldDate;
        const holdDateOverdue = requiresHoldDate && isHoldDateOverdue(order.holdUntilDate);
        return (
          <div className={styles.statusCell}>
            <div
              className={styles.statusBadge}
              style={{
                backgroundColor: order.OrderStatus?.colorCode
                  ? `${order.OrderStatus.colorCode}20`
                  : "#F59E0B20",
                borderColor: order.OrderStatus?.colorCode || "#F59E0B",
                color: order.OrderStatus?.colorCode || "#92400e",
              }}
            >
              {getStatusIcon(order.OrderStatus?.key)}
              <span>{order.OrderStatus?.label || order.orderStatusKey}</span>
            </div>
            {order.OrderStatusReason && (
              <div className={styles.statusReason}>{order.OrderStatusReason.label}</div>
            )}
            {requiresHoldDate && order.holdUntilDate && (
              <div className={`${styles.holdDate} ${holdDateOverdue ? styles.holdDateOverdue : ""}`}>
                <Clock size={12} />
                {t("orderAssignment.holdUntil")}: {order.holdUntilDate}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "address",
      header: t("orderAssignment.address"),
      width: "180px",
      render: (assignment) => (
        <div className={styles.addressCell} title={assignment.order.address}>
          {truncateText(assignment.order.address, 35) || t("common.na")}
        </div>
      ),
    },
    {
      key: "assignedUsers",
      header: t("orderAssignment.assignedUser"),
      width: "150px",
      render: (assignment) => (
        <div className={styles.assignedUsersCell}>
          <UsersIcon size={12} />
          {assignment.assignedUsers?.length > 0 ? (
            <span>{assignment.assignedUsers.map((u) => u.name).join(", ")}</span>
          ) : (
            <span className={styles.naText}>{t("orderAssignment.notAssigned")}</span>
          )}
        </div>
      ),
    },
    {
      key: "orderNumber",
      header: t("orderAssignment.orderNumber"),
      sortable: true,
      width: "150px",
      render: (assignment) => (
        <div className={styles.orderNumberCell}>
          <div className={styles.orderNumberRow}>
            <div className={styles.orderNumber}>#{assignment.order.orderNumber}</div>
            <button
              className={styles.copyButton}
              onClick={(e) => {
                e.stopPropagation();
                handleCopyToClipboard(assignment.order.orderNumber, "order", assignment.order.id);
              }}
              title="Copy order number"
            >
              {copiedOrderId === assignment.order.id ? (
                <Check size={14} className={styles.copiedIcon} />
              ) : (
                <Copy size={14} />
              )}
            </button>
          </div>
          {assignment.order.privateReference && (
            <div className={styles.privateReference}>{assignment.order.privateReference}</div>
          )}
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (assignment) => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleViewOrder(assignment.order, assignment.assignedUsers);
        }}
        className={styles.viewButton}
        title={t("common.view")}
      >
        <Eye size={14} />
      </button>
    ),
    width: "80px",
    frozen: true,
  };

  // ── Loading / error states ───────────────────────────────────────────
  if (isLoading && !hasLoadedOnce) {
    return (
      <div className={styles.myWorkQueuePage}>
        <PageHeader
          title={t("orderAssignment.myWorkQueue")}
          description={t("orderAssignment.workQueueSubtitle")}
          icon={Briefcase}
        />
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t("common.loading")}</h3>
          <p>{t("orderAssignment.loadingWorkQueue")}</p>
        </div>
      </div>
    );
  }

  if (loadingError && !hasLoadedOnce) {
    return (
      <div className={styles.myWorkQueuePage}>
        <PageHeader
          title={t("orderAssignment.myWorkQueue")}
          description={t("orderAssignment.workQueueSubtitle")}
          icon={Briefcase}
        />
        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t("orderAssignment.unableToLoadWorkQueue")}</h3>
          <p>{loadingError}</p>
          <button onClick={() => { fetchOrders(); fetchStats(); }} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className={styles.myWorkQueuePage}>
      <PageHeader
        title={t("orderAssignment.myWorkQueue")}
        description={t("orderAssignment.workQueueSubtitle")}
        icon={Briefcase}
      />

      {/* Shop and View Selectors */}
      <div className={styles.topSelectors}>
        {userShops.length > 1 && (
          <div className={styles.shopSelector}>
            <label htmlFor="shopSelect" className={styles.selectorLabel}>
              <Store size={16} />
              {t("common.shop")}
            </label>
            <div className={styles.selectWrapper}>
              <select
                id="shopSelect"
                value={selectedShopId}
                onChange={(e) => handleShopChange(e.target.value)}
                className={styles.selectorSelect}
              >
                {userShops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
              <ChevronDown className={styles.selectArrow} size={16} />
            </div>
          </div>
        )}

        {canViewAll && (
          <div className={styles.viewSelector}>
            <label htmlFor="viewSelect" className={styles.selectorLabel}>
              <Layers size={16} />
              {t("orderAssignment.view")}
            </label>
            <div className={styles.selectWrapper}>
              <select
                id="viewSelect"
                value={selectedView}
                onChange={(e) => handleViewChange(e.target.value)}
                className={styles.selectorSelect}
              >
                <option value="assigned">{t("orderAssignment.assignedToMe")}</option>
                <option value="all">{t("orderAssignment.allOrders")}</option>
              </select>
              <ChevronDown className={styles.selectArrow} size={16} />
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <StatsCards
          stats={statsCardsData}
          clickable
          columns={statsCardsData.length > 5 ? 6 : statsCardsData.length}
          minColumnWidth="180px"
        />
      )}

      {/* Bulk Actions Bar */}
      {selectedOrders.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <div className={styles.bulkActionsLeft}>
            <span className={styles.selectedCount}>
              {t("orderAssignment.ordersSelected", { count: selectedOrders.length })}
            </span>
          </div>
          <div className={styles.bulkActionsRight}>
            {/* Assign bulk action — only in "all" view for users with assign_orders */}
            {selectedView === "all" && canAssign && (
              <button onClick={handleBulkAssign} className={styles.bulkAssignButton}>
                <UsersIcon size={14} />
                {t("orderAssignment.assignOrders") || "Assign"}
              </button>
            )}
            <button onClick={handleBulkStatusUpdate} className={styles.bulkActionButton}>
              {t("orderAssignment.updateStatus")}
            </button>
            <button onClick={() => setSelectedOrders([])} className={styles.bulkCancelButton}>
              {t("orderAssignment.clearSelection")}
            </button>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t("common.export")}
        searchConfig={{
          show: true,
          value: searchInput,
          onChange: handleSearchChange,
          placeholder: t("orderAssignment.searchPlaceholder"),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      {/* Data Table */}
      <DataTable
        data={orders.map((assignment) => ({ ...assignment, id: assignment.order.id }))}
        columns={columns}
        actions={tableActions}
        selectable
        selectedRows={selectedOrders}
        onSelectionChange={setSelectedOrders}
        onRowClick={(assignment) => handleViewOrder(assignment.order, assignment.assignedUsers)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters()
            ? t("orderAssignment.noOrdersFound")
            : t("orderAssignment.noOrdersAssigned")
        }
        emptyDescription={
          hasActiveFilters()
            ? t("orderAssignment.noOrdersFoundDescription")
            : t("orderAssignment.noOrdersAssignedDescription")
        }
        emptyIcon={<Briefcase size={64} />}
        emptyAction={
          hasActiveFilters() && (
            <button onClick={handleClearFilters} className={styles.secondaryButton}>
              {t("common.clearFilters")}
            </button>
          )
        }
      />

      {/* Modals */}
      {showDetailsModal && selectedOrderDetails && (
        <OrderDetailsModal
          order={selectedOrderDetails}
          availableStatuses={getAvailableStatuses()}
          orderStatusReasons={referenceData.orderStatusReasons}
          cities={referenceData.cities}
          isResellerDropshipper={isResellerDropshipper}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrderDetails(null);
          }}
          onStatusUpdate={handleStatusUpdate}
          onUpdate={handleUpdateOrder}
          assignedUsers={selectedAssignedUsers}
        />
      )}

      {showBulkUpdateModal && (
        <BulkStatusUpdateModal
          selectedOrderIds={selectedOrders}
          selectedOrders={orders
            .filter((a) => selectedOrders.includes(a.order.id))
            .map((a) => a.order)}
          availableStatuses={getAvailableStatuses()}
          orderStatusReasons={referenceData.orderStatusReasons}
          onClose={() => setShowBulkUpdateModal(false)}
          onUpdate={handleBulkStatusUpdateComplete}
        />
      )}

      {showBulkAssignModal && (
        <BulkAssignModal
          isOpen={showBulkAssignModal}
          onClose={() => setShowBulkAssignModal(false)}
          onConfirm={handleBulkAssignConfirm}
          selectedCount={selectedOrders.length}
          users={allUsers}
          isLoading={isBulkAssigning}
        />
      )}

      {noteTooltip && (
        <div
          className={styles.specialNoteTooltip}
          style={{ top: noteTooltip.top, left: noteTooltip.left }}
        >
          {noteTooltip.text}
        </div>
      )}
    </div>
  );
};

export default MyWorkQueuePage;